import "./login.css";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import RareBoxLogo from "../../../Logos/RareBoxLogo";
import { useEffect, useState } from "react";
import { GlobalState } from "../../../context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import ForgotPasswordModal from "./ForgotPasswordModal/ForgotPasswordModal";

const getAuth2Payload = (data) => data?.result;

const getChallengeIdFromResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.challengeId || payload?.challenge_id || payload?.id || "";
};

const getExpiryFromResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.expiresAt || payload?.expires_at || "";
};

const getUserFromVerifyResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.user || data?.user;
};

const getPyTokenFromVerifyResponse = (data) => {
	const payload = getAuth2Payload(data);
	return payload?.pyToken || payload?.py_token || data?.pyToken || data?.py_token;
};

const Login = () => {
	const { snackBar, setSnackBar, setSnackBarOpen, setLoginUser, setLoadingOpen } = GlobalState();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (localStorage.getItem("user_token")) navigate("/explore");
	}, []);

	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [otp, setOtp] = useState("");
	const [challengeId, setChallengeId] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [step, setStep] = useState("login");

	const showSnackBar = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const resetOtpState = () => {
		setOtp("");
		setChallengeId("");
		setExpiresAt("");
		setStep("login");
	};

	const completeLogin = (user, pyToken) => {
		setLoginUser(user);
		localStorage.setItem("user_token", user.token);
		localStorage.setItem("user", JSON.stringify(user));
		axios.defaults.headers.common["Authorization"] = `Bearer ${user.token}`;
		if (pyToken) {
			localStorage.setItem("pyToken", pyToken);
		}
		navigate("/explore");
	};

	const formatExpiry = (value) => {
		if (!value) return "";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return "";
		return date.toLocaleString();
	};

	const handleLoginClick = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);
		if (userName === "" || password === "") {
			showSnackBar("Enter both email and password");
			setLoadingOpen(false);
			return;
		}

		try {
			const res = await axios.post("/api/auth2/login", { email: userName, password });
			const nextChallengeId = getChallengeIdFromResponse(res?.data);
			const nextExpiresAt = getExpiryFromResponse(res?.data);
			if (!nextChallengeId) {
				throw new Error("Login response did not include challengeId");
			}
			setChallengeId(nextChallengeId);
			setExpiresAt(nextExpiresAt);
			setOtp("");
			setStep("otp");
			showSnackBar(res.data.message || "OTP sent to your email", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();
		if (otp.trim() === "") {
			showSnackBar("Enter the OTP sent to your email");
			return;
		}

		setLoadingOpen(true);
		try {
			const res = await axios.post("/api/auth2/verify_otp", {
				challengeId,
				otp: otp.trim(),
			});
			const verifiedUser = getUserFromVerifyResponse(res?.data);
			const pyToken = getPyTokenFromVerifyResponse(res?.data);
			if (!verifiedUser?.token) {
				throw new Error("OTP verification response did not include user token");
			}
			completeLogin(verifiedUser, pyToken);
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleResendOtp = async () => {
		setLoadingOpen(true);
		try {
			const res = await axios.post("/api/auth2/resend_otp", { challengeId });
			const nextChallengeId = getChallengeIdFromResponse(res?.data);
			const nextExpiresAt = getExpiryFromResponse(res?.data);
			if (!nextChallengeId) {
				throw new Error("Resend OTP response did not include challengeId");
			}
			setChallengeId(nextChallengeId);
			setExpiresAt(nextExpiresAt);
			setOtp("");
			showSnackBar(res.data.message || "OTP resent to your email", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Box className="login-page-shell" display="flex" alignItems="center" justifyContent="center">
			<Box
				className="login-layout"
				sx={{
					width: { xs: "100%", md: "min(1120px, 92vw)" },
					minHeight: { xs: "calc(100dvh - 36px)", md: "78vh" },
				}}>
				<Box className="login-brand-panel" display={{ xs: "none", md: "flex" }}>
					<Box className="login-brand-inner">
						<Box className="login-brand-logo">
							<RareBoxLogo width="100%" />
						</Box>
					</Box>
				</Box>
				<Box component="section" className="login-form-panel">
					<Box className="login-form-wrap">
						<Box sx={{ width: { xs: "180px", md: "160px" } }} display={{ xs: "block", md: "none" }} alignSelf="center">
							<RareBoxLogo width="100%" />
						</Box>
						<Stack spacing={0.5}>
							<Typography className="login-form-title">{step === "login" ? "Sign in" : "Verify"}</Typography>
							{step === "otp" ? (
								<Typography className="login-form-subtitle">{`Enter the OTP sent to ${userName}.`}</Typography>
							) : null}
						</Stack>
						<Stack
							component="form"
							onSubmit={step === "login" ? handleLoginClick : handleVerifyOtp}
							className="login-form-card"
							spacing={2.25}>
							{step === "login" ? (
								<>
									<TextField
										onChange={(e) => setUserName(e.target.value)}
										value={userName}
										size="small"
										id="login-email-input"
										label="Email"
										type="text"
										autoComplete=""
										fullWidth
										className="login-input"
									/>
									<TextField
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										size="small"
										id="login-password-input"
										label="Password"
										type="password"
										autoComplete=""
										fullWidth
										className="login-input"
									/>
									<Button variant="contained" fullWidth type="submit" className="login-primary-btn">
										Continue
									</Button>
									<Typography className="login-link-row">
										Forgot password?
										<span className="login-inline-link" onClick={() => setOpen(true)}>
											Reset
										</span>
									</Typography>
								</>
							) : (
								<>
									{expiresAt ? (
										<Typography className="login-expiry-text">OTP expires at {formatExpiry(expiresAt)}</Typography>
									) : null}
									<TextField
										value={otp}
										onChange={(e) => setOtp(e.target.value)}
										size="small"
										id="login-otp-input"
										label="OTP"
										type="text"
										autoComplete="one-time-code"
										fullWidth
										className="login-input"
									/>
									<Button variant="contained" fullWidth type="submit" className="login-primary-btn">
										Verify OTP
									</Button>
									<Button
										variant="outlined"
										fullWidth
										type="button"
										onClick={handleResendOtp}
										className="login-secondary-btn">
										Resend OTP
									</Button>
									<Button
										variant="text"
										fullWidth
										type="button"
										onClick={resetOtpState}
										className="login-tertiary-btn">
										Back to login
									</Button>
								</>
							)}
						</Stack>
					</Box>
				</Box>
			</Box>
			<ForgotPasswordModal open={open} setOpen={setOpen} />
		</Box>
	);
};

export default Login;
